//library
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

import api from "services/api";

//dto
import ErrorResponseDto from "dto/services/error-response-dto";
import { PageCareerTableDto } from "dto/pages/career/page-career-dto";
import CareerTableProps, { CareerDataDto, CareerUpdateDto } from "dto/components/pages/career/page-career-table-dto";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//constants
import { jobTypes } from "constants/job-types-options";
import { statusOptions } from "constants/status-options";

//common
import formatIndexNumber from "common/format-index-number";
import serveRequestErrors from "common/serve-request-errors";

//assets
import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";

const CareerTable: React.FC<CareerTableProps> = ({ jobs, setDisableAddNew, newRow, setNewRow, onUpdateCareers, onCreateCareer, onHandlePageChange, fetching, paginationTable }) => {
	const tableRef = useRef<HTMLDivElement | null>(null);
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [careerData, setCareerData] = useState<CareerDataDto>(jobs);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const [careerUpdatedData, setCareerUpdatedData] = useState<CareerUpdateDto>();
	const isEmptyData = useMemo(() => careerData.length === 0 && !fetching, [careerData.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "title" | "employmentType" | "description" | "document" | "displayStatus", value: string) => {
		const updatedCareer = careerUpdatedData ?? careerData.find((career) => career.id === id);

		if (updatedCareer) {
			const updatedData = { ...updatedCareer, [field]: value };

			setCareerUpdatedData(updatedData);

			const requiredFields = ["title", "employmentType", "description", "displayStatus"];

			if (requiredFields.includes(field)) {
				setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" } }));
			}
		}
	}, [careerData, careerUpdatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["title", "employmentType", "description", "displayStatus"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (careerUpdatedData) {
			const rowErrors = validateRow(careerUpdatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newCareer = careerData.map((career) => (career.id === id ? { ...career, ...careerUpdatedData } : career));

			setCareerData(newCareer);
			onUpdateCareers(careerUpdatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setCareerUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [careerData, careerUpdatedData, onUpdateCareers, setDisableAddNew]);

	//prettier-ignore
	const updateCareerData = useCallback((updatedCareer: PageCareerTableDto, newCareerData: CareerDataDto, newSortNumber?: number) => {
		const updatedData = { ...updatedCareer, sortNumber: newSortNumber ?? updatedCareer.sortNumber };
		setCareerUpdatedData(updatedData);
		setCareerData(newCareerData);
	}, [setCareerUpdatedData, setCareerData]);

	//prettier-ignore
	const handlePageMovement = useCallback(async (updatedCareer: PageCareerTableDto, targetPage: number, isNextPage: boolean, paginationTable: { currentPage: number; totalPages: number; tableSize: number }) => {
		try {
			const payload = { size: paginationTable.tableSize, page: targetPage };
			const response = await api.get.career.careers(payload);
			const pageData = response?.data?.data?.list?.content || [];

			if (pageData.length > 0) {
				const targetItem = isNextPage ? pageData[0] : pageData[pageData.length - 1];
				const newCareerData = isNextPage ? [updatedCareer, ...pageData.slice(1)] : [...pageData.slice(0, -1), updatedCareer];
				updateCareerData(updatedCareer, newCareerData, targetItem.sortNumber);
				onHandlePageChange(targetPage);
			}
		} catch (error) {
			const err = error as Error | ErrorResponseDto;
			serveRequestErrors(err);
		}
	}, [updateCareerData, onHandlePageChange]);

	//prettier-ignore
	const onHandleMove = useCallback(async (id: string, move: number) => {
		const index = careerData.findIndex((career) => career.id === id);
		const updatedCareer = careerUpdatedData ?? careerData[index];

		const isFirstItem = index === 0 && move === -1;
		const isLastItem = index === careerData.length - 1 && move === 1;
		const moveToPrevPage = isFirstItem && paginationTable.currentPage > 0;
		const moveToNextPage = isLastItem && paginationTable.currentPage < paginationTable.totalPages - 1;

		if (!isFirstItem && !isLastItem) {
			const newCareer = [...careerData];
			newCareer.splice(index, 1);
			const movedDataSortNumber = careerData[index + move].sortNumber;

			updateCareerData(updatedCareer, newCareer, movedDataSortNumber);
			newCareer.splice(index + move, 0, updatedCareer);
		} else if (moveToPrevPage) {
			await handlePageMovement(updatedCareer, paginationTable.currentPage - 1, false, paginationTable);
		} else if (moveToNextPage) {
			await handlePageMovement(updatedCareer, paginationTable.currentPage + 1, true, paginationTable);
		}
	}, [careerData, careerUpdatedData, paginationTable, updateCareerData, handlePageMovement]);

	//prettier-ignore
	const onHandleDelete = useCallback((id: string) => {
		const updatedCareer = careerData.find((career) => career.id === id);

		if (updatedCareer) {
			const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

			onUpdateCareers(id, changePage);
		}

		setEditingRow(null);
		setCareerUpdatedData(null);
		setDisableAddNew(false);
    }, [careerData, paginationTable, onUpdateCareers, setDisableAddNew]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newCareer = {
			title: newRow?.title || "",
			employmentType: newRow?.employmentType || "",
			description: newRow?.description || "",
			document: newRow?.document || "",
			displayStatus: newRow?.displayStatus || "",
			isHiring: true,
			status: "active",
		};

		onCreateCareer(newCareer);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		const option = options.find((option) => option.value === value);

		return option ? option.label : value;
	};

	const cancelEditing = useCallback(() => {
		setEditingRow(null);
		setCareerUpdatedData(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, [editingRow || ""]: {} }));
	}, [setDisableAddNew, editingRow]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (editingRow && tableRef.current && !tableRef.current.contains(event.target as Node)) cancelEditing();
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [editingRow, cancelEditing]);

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setCareerData(jobs);
	}, [careerData, editingRow, isReadyData, jobs]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-career-table">
			<div className="career-table">
				<div className="career-table__wrapper" ref={tableRef}>
					<table className="career-table__container">
						<thead className="career-table__header">
							<tr>
								<th className="career-table__th">No.</th>
								<th className="career-table__th">Job Title</th>
								<th className="career-table__th">Job Type</th>
								<th className="career-table__th">Description</th>
								<th className="career-table__th">Job Details Document URL</th>
								<th className="career-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="career-table__content">
							{careerData.map((job, index) => {
								const isEditing = editingRow === job.id;
								const isExpanded = expandedRows.includes(job.id);
								const rowErrors = errors[job.id] || {};

								return (
									<tr className="career-table__tr" key={job.id}>
										<td className="career-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="career-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!careerUpdatedData} value={careerUpdatedData?.title ?? job.title} maxCharLength={90} rows={5} maxRows={20} onChange={(e) => onHandleInputChange(job.id, "title", e.target.value)} />
											) : isExpanded ? (
												job.title
											) : (
												truncateText(job.title, 30)
											)}
										</td>

										<td className="career-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="employmentType" error={rowErrors.employmentType} touched={!!careerUpdatedData} placeholder={job.employmentType} value={careerUpdatedData?.employmentType ?? job.employmentType} onChange={(e) => onHandleInputChange(job.id, "employmentType", e.target.value)} options={jobTypes} searchable={false} />
											) : (
												getLabel(jobTypes, job.employmentType)
											)}
										</td>

										<td className="career-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="description" error={rowErrors.description} touched={!!careerUpdatedData} value={careerUpdatedData?.description ?? job.description} maxCharLength={500} rows={5} maxRows={60} onChange={(e) => onHandleInputChange(job.id, "description", e.target.value)} />
											) : isExpanded ? (
												job.description
											) : (
												truncateText(job.description, 100)
											)}
										</td>

										<td className="career-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea error={rowErrors.document} touched={!!careerUpdatedData} name="document" value={careerUpdatedData?.document ??job.document} maxRows={100} rows={5} onChange={(e) => onHandleInputChange(job.id, "document", e.target.value)} />
											) : isExpanded ? (
												job.document
											) : (
												truncateText(job.document ?? "", 80)
											)}
										</td>

										<td className="career-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="displayStatus" error={rowErrors.displayStatus} touched={!!careerUpdatedData} placeholder={job.displayStatus} value={careerUpdatedData?.displayStatus ?? job.displayStatus} onChange={(e) => onHandleInputChange(job.id, "displayStatus", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={job.displayStatus === "active" ? "career-table__td--active" : "career-table__td--inactive"}>{getLabel(statusOptions, job.displayStatus)}</span>
											)}
										</td>

										<td className="career-table__td">
											{isEditing ? (
												<div className="career-table__edit">
													<button className="career-table__icon-button--bordered" onClick={() => onHandleDelete(job.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button>

													<button className="career-table__icon-button--bordered" onClick={() => onHandleMove(job.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="career-table__icon-button--bordered" onClick={() => onHandleMove(job.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button>

													<button className="career-table__icon-button--save" onClick={() => onHandleSave(job.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="career-table__actions">
													<button className="career-table__icon-button" onClick={() => toggleRow(job.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="career-table__icon-button" onClick={() => onHandleEdit(job.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="career-table__tr" ref={newRowRef}>
									<td className="career-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="career-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-title" error={errors["new"]?.title} touched={!!newRow} fieldRequired value={newRow?.title || ""} maxCharLength={90} rows={5} maxRows={20} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="career-table__td">
										{/* prettier-ignore */}
										<AppSelectInput name="new-employmentType" error={errors["new"]?.employmentType} touched={!!newRow} fieldRequired placeholder="Please Select" value={newRow?.employmentType || ""} onChange={(e) => onHandleNewInputChange("employmentType", e.target.value)} options={jobTypes} searchable={false} />
									</td>

									<td className="career-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-description" error={errors["new"]?.description} touched={!!newRow} fieldRequired value={newRow?.description || ""} maxCharLength={500} rows={5} maxRows={60} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("description", e.target.value)} />
									</td>

									<td className="career-table__td">
										{/* prettier-ignore */}
										<AppTextarea error={errors["new"]?.document} touched={!!newRow} name="new-document" value={newRow?.document || ""} maxRows={100} rows={5} onChange={(e) => onHandleNewInputChange("document", e.target.value)} />
									</td>

									<td className="career-table__td">
										{/* prettier-ignore */}
										<AppSelectInput name="new-displayStatus" error={errors["new"]?.displayStatus} touched={!!newRow} fieldRequired placeholder="Please Select" value={newRow?.displayStatus || ""} onChange={(e) => onHandleNewInputChange("displayStatus", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="career-table__td">
										<div className="career-table__actions">
											<button className="career-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="career-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="career-table__tr">
									<td className="career-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}
							{isEmptyData && (
								<tr className="career-table__tr">
									<td className="career-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default CareerTable;
